import { useEffect, useMemo } from 'react';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { Users, Trophy, Zap, ShieldCheck, Heart, Sparkles } from 'lucide-react';
import { SEO } from '../components/SEO';
import { useLocalizedSeo } from '../hooks/useLocalizedSeo';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import { isSubdomainEnabled, getAppDomain } from '../utils/domain';

const SITE_URL = 'https://bolaoelite.com.br';

export default function Sobre() {
  const { t } = useTranslation('institutional');

  // Mesma logica da Landing: subdominio app quando habilitado; /login
  // redireciona pra /auth (tela de cadastro/login).
  const loginUrl = isSubdomainEnabled() ? `https://${getAppDomain()}/login` : '/login';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        url: `${SITE_URL}/sobre`,
        name: 'Sobre o Bolão Elite',
        description:
          'Conheça a história, a missão e as pessoas por trás do Bolão Elite — a plataforma brasileira de bolões de futebol entre amigos.',
        inLanguage: 'pt-BR',
        publisher: {
          '@type': 'Organization',
          name: 'Bolão Elite',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Person',
        '@id': `${SITE_URL}/sobre#autor`,
        name: 'Ivan Henrique',
        jobTitle: 'Fundador e Engenheiro de Software do Bolão Elite',
        description:
          'Engenheiro de Software e fã de futebol, criou o Bolão Elite para resolver a dor de organizar bolões entre amigos sem planilha e sem confusão.',
        knowsAbout: ['Bolões de futebol', 'Engenharia de Software', 'Engenharia de produto'],
        url: `${SITE_URL}/sobre`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${SITE_URL}#organization`,
        name: 'Bolão Elite',
        url: SITE_URL,
        logo: `${SITE_URL}/logo-512.png`,
        founder: { '@id': `${SITE_URL}/sobre#autor` },
        foundingDate: '2026-01-15',
        description:
          'Plataforma brasileira de bolões de futebol entre amigos. Bolões privados ou públicos, palpites em tempo real, ranking automático.',
        sameAs: ['https://play.google.com/store/apps/details?id=com.bolaoelite.app'],
      },
    ],
    []
  );

  const seo = useLocalizedSeo('sobre');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO
        title={t('about.seo_title', 'Sobre o Bolão Elite — Quem somos, missão e história')}
        description={t('about.seo_desc', 'Conheça o Bolão Elite: a plataforma brasileira que descomplica os bolões de futebol entre amigos. Saiba quem está por trás e por que criamos o produto.')}
        canonical={seo.canonical}
        ogUrl={seo.ogUrl}
        locale={seo.locale}
        alternates={seo.alternates}
        keywords="sobre bolão elite, quem somos, fundador, missão, história, plataforma bolões"
        jsonLd={jsonLd}
      />

      <PublicHeader />

      <section className="bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <nav className="text-sm text-white/70 mb-3" aria-label="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">{t('common:nav.home', 'Início')}</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{t('about.breadcrumb', 'Sobre')}</span>
          </nav>
          <span className="inline-block text-xs font-semibold text-primary-200 uppercase tracking-widest">
            {t('about.eyebrow', 'Sobre nós')}
          </span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            {t('about.h1', 'Bolão Elite: a plataforma do bolão entre amigos')}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-3xl">
            {t('about.intro', 'O Bolão Elite nasceu para resolver uma dor simples: organizar um bolão de futebol entre amigos sem planilha do Excel, sem grupo do WhatsApp bagunçado e sem cálculo de pontuação no boteco. Um produto feito por quem joga bolão, para quem joga bolão.')}
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-12 space-y-12">
        <section>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {t('about.mission_title', 'Nossa missão')}
          </h2>
          <p className="text-base text-gray-700 leading-relaxed">
            {t('about.mission_p1', 'Queremos democratizar o bolão de futebol no Brasil. Bolão sempre foi uma tradição entre amigos, família, colegas de trabalho e turmas de faculdade — mas até pouco tempo atrás, organizar um exigia papel, planilha, paciência e um administrador disposto a fazer as contas no fim de cada rodada. O Bolão Elite automatiza tudo isso. Você cria um bolão em menos de um minuto, escolhe o campeonato, convida quem quiser, e o sistema cuida do resto: importa os jogos, calcula a pontuação rodada a rodada e gera o ranking em tempo real.')}
          </p>
          <p className="mt-4 text-base text-gray-700 leading-relaxed">
            <Trans i18nKey="about.mission_p2" t={t}>
              Nossa visão é simples: <strong>bolão entre amigos deve ser sobre futebol e diversão — não sobre planilha</strong>. E mais: deve ser acessível a qualquer pessoa, com qualquer celular, em qualquer cidade do país.
            </Trans>
          </p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card icon={<Users className="w-5 h-5" />} title={t('about.card_1_title', 'Para amigos, de verdade')}>
            {t('about.card_1_text', 'Bolões privados onde só quem você convida entra. Sem trolls, sem anônimos, sem confusão. Ideal para grupo do trabalho, família ou galera da faculdade.')}
          </Card>
          <Card icon={<Trophy className="w-5 h-5" />} title={t('about.card_2_title', 'Bolões públicos')}>
            {t('about.card_2_text', 'Quer competir com gente do Brasil inteiro? Entre em um bolão público aberto, sem precisar de convite, e dispute o ranking nacional de cada competição.')}
          </Card>
          <Card icon={<Zap className="w-5 h-5" />} title={t('about.card_3_title', 'Tempo real')}>
            {t('about.card_3_text', 'Resultados, ranking e estatísticas atualizados em tempo real. Sem esperar o administrador "fechar a rodada".')}
          </Card>
          <Card icon={<ShieldCheck className="w-5 h-5" />} title={t('about.card_4_title', '100% gratuito')}>
            {t('about.card_4_text', 'O plano gratuito tem tudo o que você precisa pra jogar bolão. Quem quiser mais funcionalidades pode assinar o Premium, mas é opcional — nada de pagar para participar.')}
          </Card>
        </section>

        <section id="autor">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {t('about.author_title', 'Quem está por trás')}
          </h2>
          <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              IH
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900">Ivan Henrique</h3>
              <p className="text-sm text-primary-700 font-semibold mt-0.5">
                {t('about.author_role', 'Fundador e Engenheiro de Software')}
              </p>
              <p className="mt-4 text-base text-gray-700 leading-relaxed">
                {t('about.author_p1', 'Engenheiro de Software com anos de experiência criando produtos digitais, Ivan é fã de futebol desde criança e jogador habitual de bolão entre amigos. A ideia do Bolão Elite nasceu de uma frustração pessoal: todo ano de Copa do Mundo, alguém da turma puxava o "vou montar a planilha" — e o caos começava. Em 2026, decidiu criar a plataforma que gostaria de ter usado.')}
              </p>
              <p className="mt-3 text-base text-gray-700 leading-relaxed">
                {t('about.author_p2', 'Cada feature do Bolão Elite passou pelo crivo de "isso ajuda meu grupo de amigos?" — desde a regra de pontuação que o criador do bolão pode customizar até o link de convite que entra em qualquer celular.')}
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {t('about.diff_title', 'O que nos diferencia')}
          </h2>
          <div className="space-y-4">
            <Bullet icon={<Heart className="w-5 h-5" />} title={t('about.diff_1_title', 'Foco em bolão entre amigos.')}>
              {t('about.diff_1_text', 'Não somos casa de apostas, não vendemos palpites e não promovemos jogo a dinheiro. Nosso público é torcedor que quer se divertir com o grupo.')}
            </Bullet>
            <Bullet icon={<Sparkles className="w-5 h-5" />} title={t('about.diff_2_title', 'Tecnologia que desaparece.')}>
              {t('about.diff_2_text', 'A maior parte das decisões técnicas — banco de dados, automação de importação de jogos, cálculo de pontuação — fica invisível pro usuário final. Ele só vê os palpites, o ranking e a competição com os amigos.')}
            </Bullet>
            <Bullet icon={<ShieldCheck className="w-5 h-5" />} title={t('about.diff_3_title', 'Transparência com dados.')}>
              {t('about.diff_3_text', 'Aderência à LGPD, política de privacidade clara, conta exclusiva sua. Você é dono do seu histórico de palpites — e pode excluir tudo a qualquer momento.')}
            </Bullet>
            <Bullet icon={<Zap className="w-5 h-5" />} title={t('about.diff_4_title', 'Mobile-first.')}>
              {t('about.diff_4_text', 'O Bolão Elite foi desenhado pra celular antes de qualquer coisa. Tudo no app Android e no site funciona perfeito no celular — o palpite no ônibus a caminho do trabalho é caso de uso central, não detalhe.')}
            </Bullet>
          </div>
        </section>

        <section className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-6 sm:p-8 text-white text-center">
          <h2 className="text-xl sm:text-2xl font-extrabold mb-3">
            {t('about.cta_title', 'Pronto pra começar?')}
          </h2>
          <p className="text-white/80 mb-5 max-w-2xl mx-auto">
            {t('about.cta_text', 'Crie sua conta gratuita em menos de um minuto, monte seu primeiro bolão e convide a galera. Sem cartão, sem pegadinha.')}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={loginUrl}
              className="inline-block px-6 py-3 bg-white text-primary-700 font-bold rounded-lg hover:bg-primary-50 transition-colors"
            >
              {t('about.cta_primary', 'Criar conta grátis')}
            </a>
            <Link
              to="/contato"
              className="inline-block px-6 py-3 bg-white/10 text-white font-bold rounded-lg hover:bg-white/20 transition-colors border border-white/30"
            >
              {t('about.cta_secondary', 'Falar com a gente')}
            </Link>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

function Card({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 hover:border-primary-200 transition-colors">
      <div className="w-10 h-10 bg-primary-100 text-primary-700 rounded-lg flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="text-base font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 leading-relaxed">{children}</p>
    </div>
  );
}

function Bullet({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 bg-primary-50 text-primary-700 rounded-lg flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <p className="text-base text-gray-700 leading-relaxed pt-1.5">
        <strong>{title}</strong> {children}
      </p>
    </div>
  );
}
